import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { getEstadoChipClass } from '../../../utils/styles';

// Importación de Pestañas
import DetalleTab from './Tabs/DetalleTab';
import EndososTab from './Tabs/EndososTab'; 
import SiniestrosTab from './Tabs/SiniestrosTab';
import RefacturacionesTab from './Tabs/RefacturacionesTab';
import CobranzaTab from './Tabs/CobranzaTab';
import DistribucionTab from './Tabs/DistribucionTab';
import PostVentaTab from './Tabs/PostVentaTab'; 

// Importación de Pestaña Reutilizada (Datos de Contacto del Cliente)
import DatosContacto from '../../pages/DetalleCliente/Tabs/DatosContacto';

// Importamos el Modal de Cliente para la edición desde la Tab Contacto
import ModalCliente from '../Clientes/ModalCliente';
import ModalNotificacion from '../../ui/ModalNotificacion';

// IMPORTAMOS EL HOOK DE AUTENTICACIÓN CENTRALIZADO (ACL)
import { useAuth } from '../../hooks/useAuth'; // ◄ Sincronización de rol nativa de Firestore

// Icono de Candado para Tabs Restringidos
const LockIcon = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
    </svg>
);

const DetallePoliza = () => {
  const { clientId, polizaId } = useParams();
  const navigate = useNavigate();
  
  const [client, setClient] = useState(null);
  const [poliza, setPoliza] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('detalle');
  
  const [showClientModal, setShowClientModal] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  // Consumimos el estado de rol y carga de sesión de forma reactiva en memoria
  const { isAdmin, loadingAuth } = useAuth(); // ◄ REEMPLAZA EL USO DE LOCALSTORAGE

  // LISTENERS EN TIEMPO REAL (Header y Datos de Pestañas)
  useEffect(() => {
    if (!clientId || !polizaId) return;

    // Referencias a los documentos
    const clientRef = doc(db, 'public/data/clientes', clientId);
    const polizaRef = doc(db, `public/data/clientes/${clientId}/polizas`, polizaId);

    // Escucha activa del Cliente
    const unsubClient = onSnapshot(clientRef, (snap) => {
      if (snap.exists()) {
        setClient({ id: snap.id, ...snap.data() });
      }
    });

    // Escucha activa de la Póliza (Esto actualiza el Header inmediatamente)
    const unsubPoliza = onSnapshot(polizaRef, (snap) => {
      if (snap.exists()) {
        setPoliza({ id: snap.id, ...snap.data() });
      }
      setLoading(false);
    }, (error) => {
      console.error("Error escuchando póliza:", error);
      setLoading(false);
    });

    // Limpieza de suscripciones al desmontar
    return () => {
      unsubClient();
      unsubPoliza();
    };
  }, [clientId, polizaId]);

  const handleClientSaveSuccess = (msg, updatedClient) => {
      setClient(updatedClient); 
      setNotification({ show: true, message: msg, type: 'success' });
      setShowClientModal(false);
  };

  // --- ESCUDO DE CARGA UNIFICADO (EVITA PARPADEOS) ---
  // El sistema permanecerá en el spinner elegante de carga hasta que se descarguen los datos
  // Y se haya verificado de forma síncrona en memoria si el usuario es Administrador.
  if (loading || loadingAuth) return (
    <div className="flex h-screen items-center justify-center bg-gray-50 select-none">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  if (!poliza || !client) return <div className="p-10 text-center text-gray-500">No se encontró la información solicitada.</div>;

  // --- LÓGICA DE VISIBILIDAD DINÁMICA ---
  const showRefacturaciones = poliza.isRefacturacion !== undefined 
      ? poliza.isRefacturacion 
      : (poliza.riesgo || '').toUpperCase().includes('CAUCI');

  // Solo se muestra si el campo postventa es exactamente "Si"
  const isPostVentaActivo = poliza.postventa === "Si";

  return (
    <div className="min-h-screen bg-gray-50 pb-10 font-sans text-left">
      
      {/* HEADER DE CONTEXTO - REACTIVO */}
      <header className="bg-white shadow-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            
            {/* LADO IZQUIERDO: Volver a Cliente + Datos */}
            <div className="flex items-center gap-4 text-left">
               <button 
                  onClick={() => navigate(`/clientes/${clientId}`)} 
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-full hover:bg-gray-100" 
                  title="Volver al Cliente"
               >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
               </button>
               <div className="text-left">
                  <h1 className="text-xl font-bold text-gray-900 leading-tight flex items-center gap-2">
                      Póliza #{poliza.nPoliza || 'S/N'}
                      <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wide border ${getEstadoChipClass(poliza.estadoEmision)}`}>
                          {poliza.estadoEmision}
                      </span>
                      {/* Indicador inmediato si el cliente arrastra deuda */}
                      {client.tieneDeuda && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-rose-50 text-rose-700 border border-rose-200 uppercase tracking-wider">
                          ⚠️Cliente con Deuda
                       </span>
                      )}
                  </h1>
                  <p className="text-sm text-gray-500 flex items-center mt-1 text-left">
                    <span className="font-medium text-gray-700 mr-1">Cliente:</span> {client.nombre} {client.apellido}
                    <span className="mx-2 text-gray-300">|</span>
                    <span className="font-medium text-blue-600">{poliza.compania}</span>
                  </p>
               </div>
            </div>

            {/* LADO DERECHO: Botón Volver Buscador */}
            <div className="flex items-center gap-2">
                <button 
                    onClick={() => navigate('/BuscadorPolizasPage')}
                    className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg text-sm font-bold transition shadow-sm active:scale-95"
                >
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                    Volver al Buscador
                </button>
            </div>

          </div>
        </div>
        
        {/* NAVEGACIÓN TABS */}
        <div className="border-t border-gray-200 bg-white select-none">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <nav className="-mb-px flex space-x-8 overflow-x-auto custom-scrollbar">
                    {[
                        { id: 'detalle', label: 'DETALLE' },
                        { id: 'contacto', label: 'CONTACTO' }, 
                        { id: 'endosos', label: 'ENDOSOS' },
                        { id: 'refacturaciones', label: 'REFACTURACIONES', hidden: !showRefacturaciones }, 
                        { id: 'siniestros', label: 'SINIESTROS' },
                        { id: 'cobranza', label: 'COBRANZA' },
                        { id: 'postventa', label: 'POSTVENTA', hidden: !isPostVentaActivo }, 
                        { id: 'distribucion', label: 'DISTRIBUCIÓN', restricted: true } 
                    ].map((tab) => {
                        if (tab.hidden) return null;

                        // Evaluamos la restricción cruzando contra la propiedad isAdmin de useAuth
                        const isLocked = tab.restricted && !isAdmin;
                        
                        return (
                            <button
                                key={tab.id}
                                onClick={() => !isLocked && setActiveTab(tab.id)}
                                disabled={isLocked}
                                className={`
                                    whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2
                                    ${activeTab === tab.id 
                                        ? 'border-blue-600 text-blue-600' 
                                        : isLocked 
                                            ? 'border-transparent text-gray-400 cursor-not-allowed'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }
                                `}
                                title={isLocked ? "Acceso restringido a Administradores" : ""}
                            >
                                {tab.label}
                                {isLocked && <LockIcon className="w-3 h-3 text-slate-400" />}
                            </button>
                        );
                    })}
                </nav>
            </div>
        </div>
      </header>

      {/* CONTENIDO PRINCIPAL */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-left">
        
        {activeTab === 'detalle' && (
            <DetalleTab 
                poliza={poliza} 
                client={client} 
            />
        )}
        
        {activeTab === 'contacto' && (
            <DatosContacto 
                client={client} 
                onEditClick={() => setShowClientModal(true)} 
            />
        )}
        
        {activeTab === 'endosos' && <EndososTab poliza={poliza} client={client} />}
        {activeTab === 'siniestros' && <SiniestrosTab poliza={poliza} client={client} />}
        {activeTab === 'refacturaciones' && showRefacturaciones && <RefacturacionesTab poliza={poliza} client={client} />}
        {activeTab === 'cobranza' && <CobranzaTab poliza={poliza} client={client} />}
        {activeTab === 'postventa' && isPostVentaActivo && <PostVentaTab poliza={poliza} client={client} />}
        
        {/* Blindaje estricto de renderizado por rol */}
        {activeTab === 'distribucion' && isAdmin && <DistribucionTab poliza={poliza} />}

      </main>

      {/* MODALES */}
      <ModalCliente 
          show={showClientModal}
          onClose={() => setShowClientModal(false)}
          client={client}
          onSaveSuccess={handleClientSaveSuccess}
          setNotification={(msg, type) => setNotification({ show: true, message: msg, type })}
      />

      <ModalNotificacion 
          show={notification.show} 
          onClose={() => setNotification({ ...notification, show: false })} 
          message={notification.message} 
          type={notification.type} 
      />

      <style>{`
          .custom-scrollbar::-webkit-scrollbar { height: 4px; }
          .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
      `}</style>
    </div>
  );
};

export default DetallePoliza;